import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import ReportingConfiguration from './components/ReportingConfiguration/ReportingConfiguration';
import { PaymentOptInBanner } from './PaymentOptInBanner';
import { Box, Center, TabPanel } from '@televet/kibble-ui/build/chakra';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { useStripe } from 'shared/hooks';
import { StripePayoutAccount } from './components/StripePayoutAccount';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  addSelectedCardBrand,
  cancelChanges,
  PaymentGrouping,
  setConfigurationStatus,
  setIncompleteFields,
  setPimsPaymentType,
  updatePaymentGrouping,
  updateSelectedEmployee,
  WritebackConfigurationStatus,
} from './state/paymentSettingsSlice';
import {
  BitwerxPaymentWritebackConfigurationCreateInput,
  BitwerxPaymentWritebackConfigurationUpdateInput,
  CardBrand,
  useGetBitwerxPaymentWritebackConfigQuery,
  useGetClinicEmployeesForPaymentSettingsQuery,
  useGetPimsPaymentTypesQuery,
  useGetTerminalLocationCountQuery,
  useUpsertOneBitwerxPaymentWritebackConfigurationMutation,
} from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums';
import { useBitwerxData, useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import UploadClinicDataForm from './components/UploadClinicDataForm';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_CLINIC_FINANCIAL_REPORT_SETTINGS } from 'shared/mutations';
import { GET_CLINIC_FINANCIAL_REPORT_SETTINGS } from 'shared/queries';
import { TabProps } from '@televet/kibble-ui/build/components/Tabs';
import { TabsLayout } from '@televet/kibble-ui/build/components/TabsLayout';
import { Text } from '@televet/kibble-ui/build/components/Text';
import TerminalConfiguration from './components/TerminalConfiguration/TerminalConfiguration';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsPaymentsEvents } from 'shared/providers/TrackingProvider/EventNames';

enum FieldNames {
  Employee = 'Employee',
  PaymentType = 'Payment Type',
  OtherCards = 'Other Cards - Payment Type',
  Amex = 'Amex - Payment Type',
  Diners = 'Diners - Payment Type',
  Discover = 'Discover - Payment Type',
  JCB = 'JCB - Payment Type',
  Mastercard = 'Mastercard - Payment Type',
  UnionPay = 'UnionPay - Payment Type',
  Visa = 'Visa - Payment Type',
}

const USERS_ALLOWED_TO_UPLOAD_ENCRYPTED_CLINIC_DATA: string[] = [
  'cl27s52cd1n4g0890q5ic7iod', // Stephanie Thomas, UVC
  'cle69g0qc15529901zqfzu9u4th', // Kaitlin Johanson, NVA
  'clkvc2hhl00ahxu01rmiqpoyq', // Kelly Gibbons, GoodVets
  'ck87hbz0638x608089dfzqqoq', // Amanda Diamond, GoodVets
  'ckk4fx4dqbyzz07820fn1ktlg', // Eric Scott, Otto (dev)
  'ckylrz9iq651k0891lbopn3gj', // Barbara Brina, Otto (dev)
  'clzirlsjn000w1a01cjywzw0c', // Paulo Lacerda, Otto (dev)
  'clacngexj5544701wtyh7jgg3j', // Kim Chilton, Otto (dev)
];

const PaymentSettings = forwardRef(
  (
    {
      setHasUnsavedChange,
    }: {
      setHasUnsavedChange: React.Dispatch<React.SetStateAction<boolean>>;
    },
    ref,
  ): JSX.Element => {
    const { currentClinicId, currentClinic, clinicUser, hasAdminRoleAtCurrentClinic } = useClinicUser();
    const { primaryIntegrationName } = useIntegrationsProvider();
    const { needsBitwerxForPaymentWritebacks, bitwerxPending, hasBitwerx, bitwerxIntegrationId } = useBitwerxData();
    const { onboardingComplete, loading } = useStripe();
    const { addFeatureFlag, isFeatureEnabled } = useFeatureFlag();
    const dispatch = useAppDispatch();
    const paymentSettings = useAppSelector((state) => state.paymentSettings);
    const { trackEvent } = useTracking();

    const { data: paymentTypesData } = useGetPimsPaymentTypesQuery({
      variables: { integrationId: bitwerxIntegrationId || '' },
      skip: !bitwerxIntegrationId || !hasAdminRoleAtCurrentClinic,
    });

    const { data: clinicEmployeeData } = useGetClinicEmployeesForPaymentSettingsQuery({
      variables: { clinicId: currentClinicId || '' },
      skip: !currentClinicId || !hasAdminRoleAtCurrentClinic,
    });

    const { data: bitwerxPaymentCofigData } = useGetBitwerxPaymentWritebackConfigQuery({
      variables: {
        integrationId: bitwerxIntegrationId || '',
      },
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
      skip: !hasAdminRoleAtCurrentClinic,
    });

    const { data: clinicSettingsData, loading: clinicSettingsLoading } = useQuery(
      GET_CLINIC_FINANCIAL_REPORT_SETTINGS,
      {
        variables: {
          id: currentClinicId,
        },
      },
    );

    const [updateFinancialReportSetting] = useMutation(UPDATE_CLINIC_FINANCIAL_REPORT_SETTINGS);

    const { data: terminalLocationCountData } = useGetTerminalLocationCountQuery({
      variables: { clinicId: currentClinicId || '' },
      skip: !currentClinicId,
    });

    const terminalLocationCount = useMemo(
      () => terminalLocationCountData?.findManyStripeTerminalLocationCount,
      [terminalLocationCountData?.findManyStripeTerminalLocationCount],
    );

    // enable writeback status column in payment report when config is first completed
    useEffect(() => {
      const hasWritebackColumnConfig =
        !!clinicSettingsData?.findUniqueClinic?.clinicSetting?.financialReportingConfig?.transactionColumns
          ?.writebackStatus;
      if (
        !clinicSettingsLoading &&
        isFeatureEnabled(FeatureFlagName.BitwerxPaymentWritebacks) &&
        !hasWritebackColumnConfig
      ) {
        updateFinancialReportSetting({
          variables: {
            where: { id: clinicSettingsData?.findUniqueClinic?.clinicSetting?.id },
            data: {
              financialReportingConfig: {
                transactionColumns: {
                  ...clinicSettingsData?.findUniqueClinic?.clinicSetting?.financialReportingConfig?.transactionColumns,
                  writebackStatus: {
                    omit: false,
                  },
                },
                disbursementColumns: {
                  ...clinicSettingsData?.findUniqueClinic?.clinicSetting?.financialReportingConfig?.disbursementColumns,
                },
              },
            },
          },
        });
      }
    }, [
      clinicSettingsData?.findUniqueClinic?.clinicSetting,
      clinicSettingsLoading,
      isFeatureEnabled,
      updateFinancialReportSetting,
    ]);

    // set initial values from stored values
    const setInitialValues = useCallback(() => {
      const writebackConfig = bitwerxPaymentCofigData?.findUniqueBitwerxPaymentWritebackConfiguration;

      if (writebackConfig) {
        if (writebackConfig.enteredById) {
          dispatch(updateSelectedEmployee(writebackConfig.enteredById));
        }

        if (writebackConfig.defaultPimsPaymentTypeId) {
          dispatch(
            setPimsPaymentType({
              cardBrand: CardBrand.Unknown,
              paymentTypeId: writebackConfig.defaultPimsPaymentTypeId,
            }),
          );
        }

        if (
          writebackConfig.amexPimsPaymentTypeId ||
          writebackConfig.discoverPimsPaymentTypeId ||
          writebackConfig.mastercardPimsPaymentTypeId ||
          writebackConfig.visaPimsPaymentTypeId
        ) {
          dispatch(updatePaymentGrouping(PaymentGrouping.CardType));
        } else {
          dispatch(updatePaymentGrouping(PaymentGrouping.SingleType));
        }

        if (writebackConfig.amexPimsPaymentTypeId) {
          dispatch(addSelectedCardBrand(CardBrand.Amex));
          dispatch(
            setPimsPaymentType({ cardBrand: CardBrand.Amex, paymentTypeId: writebackConfig.amexPimsPaymentTypeId }),
          );
        }

        if (writebackConfig.discoverPimsPaymentTypeId) {
          dispatch(addSelectedCardBrand(CardBrand.Discover));
          dispatch(
            setPimsPaymentType({
              cardBrand: CardBrand.Discover,
              paymentTypeId: writebackConfig.discoverPimsPaymentTypeId,
            }),
          );
        }

        if (writebackConfig.mastercardPimsPaymentTypeId) {
          dispatch(addSelectedCardBrand(CardBrand.Mastercard));
          dispatch(
            setPimsPaymentType({
              cardBrand: CardBrand.Mastercard,
              paymentTypeId: writebackConfig.mastercardPimsPaymentTypeId,
            }),
          );
        }

        if (writebackConfig.visaPimsPaymentTypeId) {
          dispatch(addSelectedCardBrand(CardBrand.Visa));
          dispatch(
            setPimsPaymentType({ cardBrand: CardBrand.Visa, paymentTypeId: writebackConfig.visaPimsPaymentTypeId }),
          );
        }
      }
    }, [bitwerxPaymentCofigData?.findUniqueBitwerxPaymentWritebackConfiguration, dispatch]);

    const canUploadEncryptedClinicData = useMemo(
      () => clinicUser?.id && USERS_ALLOWED_TO_UPLOAD_ENCRYPTED_CLINIC_DATA.includes(clinicUser.id),
      [clinicUser?.id],
    );

    useEffect(() => setInitialValues(), [setInitialValues]);

    // set incomplete fields
    useEffect(() => {
      if (needsBitwerxForPaymentWritebacks || bitwerxPending) {
        return;
      }

      const incompleteFields: string[] = [];

      const isPaymentTypeDeleted = (id: string): boolean => {
        const type = paymentTypesData?.findManyPimsPaymentType?.find((type) => type.id === id);
        return !!type?.isDeleted || !!type?.isInactive;
      };

      if (!paymentSettings.employeeId) {
        incompleteFields.push(FieldNames.Employee);
      } else {
        const employee = clinicEmployeeData?.findManyClinicEmployee?.find(
          (employee) => employee.id === paymentSettings.employeeId,
        );
        if (employee?.isDeleted) {
          incompleteFields.push(FieldNames.Employee);
          dispatch(updateSelectedEmployee(null));
        }
      }

      if (paymentSettings.paymentGrouping === PaymentGrouping.SingleType) {
        if (
          !paymentSettings.defaultPimsPaymentTypeId ||
          isPaymentTypeDeleted(paymentSettings.defaultPimsPaymentTypeId)
        ) {
          incompleteFields.push(FieldNames.PaymentType);
          dispatch(setPimsPaymentType({ cardBrand: CardBrand.Unknown, paymentTypeId: null }));
        }
      }

      if (paymentSettings.paymentGrouping === PaymentGrouping.CardType) {
        if (paymentSettings.selectedCardBrands.includes(CardBrand.Unknown)) {
          if (
            !paymentSettings.defaultPimsPaymentTypeId ||
            isPaymentTypeDeleted(paymentSettings.defaultPimsPaymentTypeId)
          ) {
            incompleteFields.push(FieldNames.OtherCards);
            dispatch(setPimsPaymentType({ cardBrand: CardBrand.Unknown, paymentTypeId: null }));
          }
        }

        if (paymentSettings.selectedCardBrands.includes(CardBrand.Amex)) {
          if (!paymentSettings.amexPimsPaymentTypeId || isPaymentTypeDeleted(paymentSettings.amexPimsPaymentTypeId)) {
            incompleteFields.push(FieldNames.Amex);
            dispatch(setPimsPaymentType({ cardBrand: CardBrand.Amex, paymentTypeId: null }));
          }
        }

        if (paymentSettings.selectedCardBrands.includes(CardBrand.Discover)) {
          if (
            !paymentSettings.discoverPimsPaymentTypeId ||
            isPaymentTypeDeleted(paymentSettings.discoverPimsPaymentTypeId)
          ) {
            incompleteFields.push(FieldNames.Discover);
            dispatch(setPimsPaymentType({ cardBrand: CardBrand.Discover, paymentTypeId: null }));
          }
        }

        if (paymentSettings.selectedCardBrands.includes(CardBrand.Mastercard)) {
          if (
            !paymentSettings.mastercardPimsPaymentTypeId ||
            isPaymentTypeDeleted(paymentSettings.mastercardPimsPaymentTypeId)
          ) {
            incompleteFields.push(FieldNames.Mastercard);
            dispatch(setPimsPaymentType({ cardBrand: CardBrand.Mastercard, paymentTypeId: null }));
          }
        }

        if (paymentSettings.selectedCardBrands.includes(CardBrand.Visa)) {
          if (!paymentSettings.visaPimsPaymentTypeId || isPaymentTypeDeleted(paymentSettings.visaPimsPaymentTypeId)) {
            incompleteFields.push(FieldNames.Visa);
            dispatch(setPimsPaymentType({ cardBrand: CardBrand.Visa, paymentTypeId: null }));
          }
        }
      }

      dispatch(setIncompleteFields(incompleteFields));

      if (incompleteFields.length) {
        dispatch(setConfigurationStatus(WritebackConfigurationStatus.Incomplete));
      } else if (paymentSettings.configurationStatus !== WritebackConfigurationStatus.Complete) {
        dispatch(setConfigurationStatus(WritebackConfigurationStatus.None));
      }
    }, [
      dispatch,
      needsBitwerxForPaymentWritebacks,
      bitwerxPending,
      paymentSettings.employeeId,
      paymentSettings.paymentGrouping,
      paymentSettings.selectedCardBrands,
      paymentSettings.defaultPimsPaymentTypeId,
      paymentSettings.configurationStatus,
      paymentSettings.amexPimsPaymentTypeId,
      paymentSettings.discoverPimsPaymentTypeId,
      paymentSettings.mastercardPimsPaymentTypeId,
      paymentSettings.visaPimsPaymentTypeId,
      paymentTypesData?.findManyPimsPaymentType,
      clinicEmployeeData?.findManyClinicEmployee,
    ]);

    const [upsertBitwerxWritebackConfig] = useUpsertOneBitwerxPaymentWritebackConfigurationMutation();

    const handleSaveChanges = useCallback(async () => {
      const getPaymentTypeInput = (
        pimsPaymentTypeId: string | null | undefined,
        disconnect?: boolean,
      ): { disconnect?: boolean; connect?: { id: string } | undefined } | undefined => {
        if (!pimsPaymentTypeId && !disconnect) return undefined;
        if (!pimsPaymentTypeId) return { disconnect: true };
        return { connect: { id: pimsPaymentTypeId } };
      };

      const createData: BitwerxPaymentWritebackConfigurationCreateInput = {
        integration: { connect: { id: bitwerxIntegrationId } },
        enteredBy: paymentSettings.employeeId ? { connect: { id: paymentSettings.employeeId } } : undefined,
        defaultPimsPaymentType: getPaymentTypeInput(paymentSettings.defaultPimsPaymentTypeId),
        amexPimsPaymentType: getPaymentTypeInput(paymentSettings.amexPimsPaymentTypeId),
        discoverPimsPaymentType: getPaymentTypeInput(paymentSettings.discoverPimsPaymentTypeId),
        mastercardPimsPaymentType: getPaymentTypeInput(paymentSettings.mastercardPimsPaymentTypeId),
        visaPimsPaymentType: getPaymentTypeInput(paymentSettings.visaPimsPaymentTypeId),
      };

      const updateData: BitwerxPaymentWritebackConfigurationUpdateInput = {
        integration: { connect: { id: bitwerxIntegrationId } },
        enteredBy: paymentSettings.employeeId ? { connect: { id: paymentSettings.employeeId } } : { disconnect: true },
        defaultPimsPaymentType: getPaymentTypeInput(paymentSettings.defaultPimsPaymentTypeId, true),
        amexPimsPaymentType: getPaymentTypeInput(paymentSettings.amexPimsPaymentTypeId, true),
        discoverPimsPaymentType: getPaymentTypeInput(paymentSettings.discoverPimsPaymentTypeId, true),
        mastercardPimsPaymentType: getPaymentTypeInput(paymentSettings.mastercardPimsPaymentTypeId, true),
        visaPimsPaymentType: getPaymentTypeInput(paymentSettings.visaPimsPaymentTypeId, true),
      };

      try {
        await upsertBitwerxWritebackConfig({
          variables: {
            where: {
              integrationId: bitwerxIntegrationId,
            },
            create: { ...createData },
            update: { ...updateData },
          },
        });

        const status =
          paymentSettings.employeeId && paymentSettings.defaultPimsPaymentTypeId
            ? WritebackConfigurationStatus.Complete
            : WritebackConfigurationStatus.Incomplete;

        if (status === WritebackConfigurationStatus.Complete) {
          trackEvent(SettingsPaymentsEvents.ConfigurationComplete);
          await addFeatureFlag(FeatureFlagName.BitwerxPaymentWritebacks);
        }

        dispatch(setConfigurationStatus(status));
      } catch (e) {
        console.error('Error saving writeback configuration', e);
      }
    }, [
      bitwerxIntegrationId,
      paymentSettings.employeeId,
      paymentSettings.defaultPimsPaymentTypeId,
      paymentSettings.amexPimsPaymentTypeId,
      paymentSettings.discoverPimsPaymentTypeId,
      paymentSettings.mastercardPimsPaymentTypeId,
      paymentSettings.visaPimsPaymentTypeId,
      upsertBitwerxWritebackConfig,
      dispatch,
      trackEvent,
      addFeatureFlag,
    ]);

    // reset changes if user changes clinic
    useEffect(() => {
      dispatch(cancelChanges());
      setInitialValues();
      setHasUnsavedChange(false);
    }, [currentClinicId, dispatch, setHasUnsavedChange, setInitialValues]);

    // forwarded function
    useImperativeHandle(ref, () => ({
      onLeavingCurrentPage(): void {
        dispatch(cancelChanges());
        setHasUnsavedChange(false);
      },
      onClickedSaveButton(): void {
        handleSaveChanges();
        setHasUnsavedChange(false);
      },
      onClickedCancelButton(): void {
        dispatch(cancelChanges());
        setInitialValues();
        setHasUnsavedChange(false);
      },
    }));

    const showReportingSetup = useMemo(
      () => (needsBitwerxForPaymentWritebacks || bitwerxPending || hasBitwerx) && hasAdminRoleAtCurrentClinic,
      [bitwerxPending, hasBitwerx, needsBitwerxForPaymentWritebacks, hasAdminRoleAtCurrentClinic],
    );

    const showTerminalConfig = useMemo(
      () => onboardingComplete && isFeatureEnabled(FeatureFlagName.TerminalPaymentsApp) && !!terminalLocationCount,
      [onboardingComplete, isFeatureEnabled, terminalLocationCount],
    );

    const tabList = useMemo(() => {
      const tabs: TabProps[] = [];
      if (showReportingSetup) {
        tabs.push({ label: `${primaryIntegrationName || 'PIMS'} Integration` });
      }
      if (showTerminalConfig) {
        tabs.push({ label: 'Terminal' });
      }
      return tabs;
    }, [primaryIntegrationName, showReportingSetup, showTerminalConfig]);

    const tabPanels = useMemo(() => {
      const panels: JSX.Element[] = [];
      if (showReportingSetup) {
        panels.push(
          <TabPanel key="pims-integration-reporting-config">
            <ReportingConfiguration setHasUnsavedChange={setHasUnsavedChange} />
          </TabPanel>,
        );
      }
      if (showTerminalConfig) {
        panels.push(
          <TabPanel key="terminal-config">
            <TerminalConfiguration />
          </TabPanel>,
        );
      }
      return panels;
    }, [setHasUnsavedChange, showReportingSetup, showTerminalConfig]);

    return !loading ? (
      <Box py={5}>
        {!onboardingComplete ? (
          hasAdminRoleAtCurrentClinic ? (
            <PaymentOptInBanner />
          ) : (
            <Text>Please contact an admin user to set up Payments.</Text>
          )
        ) : (
          hasAdminRoleAtCurrentClinic && <StripePayoutAccount />
        )}
        {canUploadEncryptedClinicData && <UploadClinicDataForm />}
        {currentClinic && tabList.length > 0 && <TabsLayout tabList={tabList} tabPanels={tabPanels} />}
      </Box>
    ) : (
      <Center h="100%">
        <Spinner size="xl" />
      </Center>
    );
  },
);

PaymentSettings.displayName = 'PaymentSettings';

export default PaymentSettings;
